<template>
  <a-modal
    :visible="visible"
    :width="960"
    :closable="false"
    :maskClosable="false"
    :keyboard="false"
    :footer="null"
    class="message-modal"
    centered
  >
    <!-- 右上角关闭按钮 -->
    <a-button 
      v-if="unreadCount === 0"
      type="text" 
      class="modal-close-btn"
      @click="closeModal"
    >
      <CloseOutlined />
    </a-button>

    <div class="message-wrapper">
      <!-- 左侧面板 -->
      <div class="left-panel">
        <!-- Tab切换 -->
        <div class="panel-tabs">
          <div 
            class="tab-btn" 
            :class="{ active: activeTab === 'unread' }"
            @click="switchTab('unread')"
          >
            <BellOutlined />
            <span>未读</span>
            <span v-if="unreadCount > 0" class="count">{{ unreadCount }}</span>
          </div>
          <div 
            class="tab-btn" 
            :class="{ active: activeTab === 'history' }"
            @click="switchTab('history')"
          >
            <HistoryOutlined />
            <span>历史</span>
          </div>
        </div>
        
        <!-- 消息列表 -->
        <div class="msg-list">
          <a-spin :spinning="activeTab === 'unread' ? loading : historyLoading">
            <div class="list-scroll">
              <!-- 未读消息 -->
              <template v-if="activeTab === 'unread'">
                <div
                  v-for="msg in messages"
                  :key="msg.id"
                  class="msg-card"
                  :class="{ selected: currentMessage?.id === msg.id }"
                  @click="selectMessage(msg)"
                >
                  <div class="card-dot"></div>
                  <div class="card-content">
                    <div class="card-title">{{ msg.title }}</div>
                    <div class="card-meta">
                      <span class="meta-type" :class="'type-' + msg.type">{{ msg.type_name }}</span>
                      <span class="meta-time">{{ msg.create_time_str }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="messages.length === 0 && !loading" class="empty-state">
                  <CheckCircleOutlined class="empty-icon success" />
                  <p>暂无未读消息</p>
                </div>
              </template>
              
              <!-- 历史消息 -->
              <template v-else>
                <div
                  v-for="msg in historyMessages"
                  :key="msg.id"
                  class="msg-card history"
                  :class="{ selected: currentMessage?.id === msg.id }"
                  @click="selectMessage(msg)"
                >
                  <div class="card-content">
                    <div class="card-title">{{ msg.title }}</div>
                    <div class="card-meta">
                      <span class="meta-type" :class="'type-' + msg.type">{{ msg.type_name }}</span>
                      <span class="meta-time">{{ msg.create_time_str }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="historyMessages.length === 0 && !historyLoading" class="empty-state">
                  <InboxOutlined class="empty-icon" />
                  <p>暂无历史消息</p>
                </div>
              </template>
            </div>
          </a-spin>
        </div>
      </div>

      <!-- 左右分隔条 -->
      <div class="panel-divider"></div>

      <!-- 右侧详情 -->
      <div class="right-panel">
        <template v-if="currentMessage">
          <!-- 详情头部 -->
          <div class="detail-head">
            <div class="head-title">{{ currentMessage.title }}</div>
            <div class="head-info">
              <a-tag :color="getTypeColor(currentMessage.type)" size="small">
                {{ currentMessage.type_name }}
              </a-tag>
              <span class="info-item">
                <UserOutlined /> {{ currentMessage.admin_name || '系统' }}
              </span>
              <span class="info-item">
                <ClockCircleOutlined /> {{ currentMessage.create_time_str }}
              </span>
            </div>
          </div>
          
          <!-- 详情内容 -->
          <div class="detail-body">
            <!-- 文字 -->
            <div v-if="currentMessage.type === 1" class="body-text">
              {{ currentMessage.content }}
            </div>
            
            <!-- 图片 -->
            <div v-else-if="currentMessage.type === 2" class="body-media">
              <p v-if="currentMessage.content" class="media-desc">{{ currentMessage.content }}</p>
              <a-image
                v-if="currentMessage.media_url"
                :src="currentMessage.media_url"
                class="media-img"
              />
            </div>
            
            <!-- 视频 -->
            <div v-else-if="currentMessage.type === 3" class="body-media">
              <p v-if="currentMessage.content" class="media-desc">{{ currentMessage.content }}</p>
              <video
                v-if="currentMessage.media_url"
                :src="currentMessage.media_url"
                controls
                class="media-video"
              />
            </div>
            
            <!-- 链接 -->
            <div v-else-if="currentMessage.type === 4" class="body-link">
              <p v-if="currentMessage.content" class="link-desc">{{ currentMessage.content }}</p>
              <a
                v-if="currentMessage.media_url"
                :href="currentMessage.media_url"
                target="_blank"
                class="link-btn"
              >
                <LinkOutlined /> 点击访问
              </a>
            </div>
            
            <!-- 福利展示 -->
            <div v-if="currentMessage.has_benefits === 1 && parsedBenefits" class="benefits-section">
              <div class="benefits-title">
                <GiftOutlined />
                <span>附带福利</span>
              </div>
              <div class="benefits-list">
                <!-- G币 -->
                <div v-if="parsedBenefits.gmoney?.enabled" class="benefit-item gmoney">
                  <div class="benefit-icon">
                    <DollarOutlined />
                  </div>
                  <div class="benefit-info">
                    <div class="benefit-name">G币奖励</div>
                    <div class="benefit-value">+{{ parsedBenefits.gmoney.amount }} G币</div>
                  </div>
                </div>
                
                <!-- 恐龙 -->
                <div v-if="parsedBenefits.dino?.enabled" class="benefit-item dino">
                  <div class="benefit-icon">
                    <ThunderboltOutlined />
                  </div>
                  <div class="benefit-info">
                    <div class="benefit-name">恐龙奖励</div>
                    <div class="benefit-details">
                      <a-tag v-for="(item, idx) in parsedBenefits.dino.items" :key="idx" color="green">
                        {{ item.type_cn }} x{{ item.amount }}
                      </a-tag>
                    </div>
                    <div v-if="parsedBenefits.dino.expire_days" class="benefit-expire">
                      有效期：{{ parsedBenefits.dino.expire_days }}天
                    </div>
                  </div>
                </div>
                
                <!-- 能力 -->
                <div v-if="parsedBenefits.ability?.enabled" class="benefit-item ability">
                  <div class="benefit-icon">
                    <StarOutlined />
                  </div>
                  <div class="benefit-info">
                    <div class="benefit-name">能力奖励</div>
                    <div class="benefit-details">
                      <a-tag v-for="(item, idx) in parsedBenefits.ability.items" :key="idx" color="purple">
                        {{ item }}
                      </a-tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 详情底部操作 -->
          <div class="detail-foot" v-if="activeTab === 'unread'">
            <!-- 有福利需要先领取 -->
            <template v-if="currentMessage.has_benefits === 1 && !currentMessage.benefits_claimed">
              <a-button 
                type="primary" 
                size="large" 
                block 
                @click="claimBenefits" 
                :loading="claiming"
                class="claim-btn"
              >
                <GiftOutlined /> 领取福利
              </a-button>
            </template>
            <!-- 已领取或无福利则显示已知晓 -->
            <template v-else>
              <a-button type="primary" size="large" block @click="markAsRead" :loading="marking">
                <CheckOutlined /> 我已知晓
              </a-button>
            </template>
          </div>
        </template>
        
        <!-- 未选择消息 -->
        <template v-else>
          <div class="no-select">
            <div class="no-select-icon">
              <MailOutlined />
            </div>
            <p class="no-select-text">选择左侧消息查看详情</p>
          </div>
        </template>
      </div>
    </div>

    <!-- 底部状态栏 -->
    <div class="status-bar" :class="{ warning: unreadCount > 0 }">
      <div class="status-text">
        <template v-if="unreadCount > 0">
          <ExclamationCircleOutlined />
          <span>您有 <strong>{{ unreadCount }}</strong> 条未读消息需要处理</span>
        </template>
        <template v-else>
          <CheckCircleOutlined />
          <span>所有消息已处理完毕</span>
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { 
  CheckOutlined, 
  LinkOutlined, 
  ExclamationCircleOutlined, 
  CheckCircleOutlined,
  BellOutlined,
  HistoryOutlined,
  InboxOutlined,
  UserOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CloseOutlined,
  GiftOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  StarOutlined
} from '@ant-design/icons-vue'
import useMessageStore from '@/stores/message'

const { proxy } = getCurrentInstance()
const messageStore = useMessageStore()
const { visible, messages, historyMessages, currentMessage, loading, historyLoading, activeTab } = storeToRefs(messageStore)
const marking = ref(false)
const claiming = ref(false)

// 未读数量
const unreadCount = computed(() => messages.value.length)

// 解析福利JSON
const parsedBenefits = computed(() => {
  if (!currentMessage.value?.benefits) return null
  try {
    if (typeof currentMessage.value.benefits === 'string') {
      return JSON.parse(currentMessage.value.benefits)
    }
    return currentMessage.value.benefits
  } catch (e) {
    console.error('解析福利信息失败:', e)
    return null
  }
})

// 调试：监听visible变化
watch(visible, (newVal) => {
  console.log('MessageModal visible changed:', newVal)
}, { immediate: true })

// 获取用户SteamID
const getSteamId = () => {
  return proxy.$user()?.SteamID || ''
}

// 切换tab
const switchTab = (tab) => {
  messageStore.setActiveTab(tab)
  if (tab === 'history') {
    messageStore.fetchHistoryMessages(getSteamId())
  }
}

// 选择消息
const selectMessage = (msg) => {
  messageStore.selectMessage(msg)
}

// 获取类型颜色
const getTypeColor = (type) => {
  const colors = {
    1: 'blue',
    2: 'green',
    3: 'purple',
    4: 'orange'
  }
  return colors[type] || 'default'
}

// 标记已读
const markAsRead = async () => {
  if (!currentMessage.value) return
  
  marking.value = true
  const steamid = getSteamId()
  const success = await messageStore.markAsRead(currentMessage.value.id, steamid)
  marking.value = false
  
  if (success) {
    if (messages.value.length > 0) {
      messageStore.selectMessage(messages.value[0])
    }
  }
}

// 领取福利
const claimBenefits = async () => {
  if (!currentMessage.value) return
  
  claiming.value = true
  const steamid = getSteamId()
  const success = await messageStore.claimBenefits(currentMessage.value.id, steamid)
  claiming.value = false
  
  if (success) {
    // 领取成功后更新当前消息状态
    currentMessage.value.benefits_claimed = 1
  }
}

// 关闭弹窗
const closeModal = () => {
  if (messages.value.length === 0) {
    messageStore.forceHideModal()
  }
}
</script>

<style lang="scss" scoped>
.message-modal {
  :deep(.ant-modal-content) {
    padding: 0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(102, 126, 234, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
  }
  
  :deep(.ant-modal-header) {
    display: none;
  }
  
  :deep(.ant-modal-body) {
    padding: 0;
  }
}

// 关闭按钮
.modal-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    color: #ef4444;
    background: #fff;
  }
}

// 主体内容
.message-wrapper {
  display: flex;
  height: 520px;
  background: #f8fafc;
}

// 左侧面板
.left-panel {
  width: 340px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  flex-direction: column;
  position: relative;
}

// Tab切换
.panel-tabs {
  display: flex;
  padding: 16px;
  gap: 10px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  
  .tab-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 18px;
    border-radius: 10px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
    border: 2px solid #cbd5e1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    
    &:hover {
      color: #6366f1;
      background: #eef2ff;
      border-color: #a5b4fc;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(99, 102, 241, 0.15);
    }
    
    &.active {
      color: #fff;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
      border-color: transparent;
      
      .count {
        background: #fff;
        color: #6366f1;
        font-weight: 600;
      }
    }
    
    .count {
      min-width: 22px;
      height: 22px;
      padding: 0 7px;
      border-radius: 11px;
      background: linear-gradient(135deg, #ef4444, #f97316);
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(239, 68, 68, 0.4);
    }
  }
}

// 消息列表
.msg-list {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  
  :deep(.ant-spin-nested-loading) {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  :deep(.ant-spin-container) {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 3px;
    }
  }
}

// 消息卡片
.msg-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px;
  margin-bottom: 10px;
  background: #ffffff;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #94a3b8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  
  &:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #c7d2fe;
  }
  
  &.selected {
    border-color: #6366f1;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(168, 85, 247, 0.06) 100%);
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.15), inset 0 0 0 1px rgba(99, 102, 241, 0.1);
    
    .card-title {
      color: #6366f1;
    }
  }
  
  &.history {
    opacity: 0.7;
    border-style: dashed;
    
    .card-dot {
      display: none;
    }
    
    &:hover {
      opacity: 1;
    }
  }
  
  .card-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ef4444, #f97316);
    margin-top: 4px;
    flex-shrink: 0;
    animation: pulse 2s infinite;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  }
  
  .card-content {
    flex: 1;
    min-width: 0;
    
    .card-title {
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.4;
    }
    
    .card-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .meta-type {
        font-size: 11px;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 6px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        
        &.type-1 { background: linear-gradient(135deg, #dbeafe, #e0f2fe); color: #2563eb; }
        &.type-2 { background: linear-gradient(135deg, #dcfce7, #d1fae5); color: #16a34a; }
        &.type-3 { background: linear-gradient(135deg, #f3e8ff, #fae8ff); color: #9333ea; }
        &.type-4 { background: linear-gradient(135deg, #fef3c7, #ffedd5); color: #ea580c; }
      }
      
      .meta-time {
        font-size: 12px;
        color: #94a3b8;
      }
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// 空状态
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 280px;
  color: #64748b;
  
  .empty-icon {
    font-size: 56px;
    margin-bottom: 20px;
    opacity: 0.4;
    
    &.success {
      color: #22c55e;
      opacity: 1;
      animation: bounce 1s ease-in-out;
    }
  }
  
  p {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
  }
}

@keyframes bounce {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

// 左右分隔条
.panel-divider {
  width: 2px;
  background: linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 50%, #e2e8f0 100%);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.05);
}

// 右侧面板
.right-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
}

// 详情头部
.detail-head {
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03);
  
  .head-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 14px;
    line-height: 1.4;
    letter-spacing: -0.02em;
  }
  
  .head-info {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    
    .info-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #64748b;
      padding: 4px 0;
      
      :deep(.anticon) {
        font-size: 14px;
        opacity: 0.7;
      }
    }
    
    :deep(.ant-tag) {
      margin: 0;
      padding: 4px 12px;
      border-radius: 6px;
      font-weight: 600;
      border: none;
    }
  }
}

// 详情内容
.detail-body {
  flex: 1;
  padding: 28px;
  overflow-y: auto;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  
  .body-text {
    font-size: 15px;
    line-height: 2;
    color: #334155;
    white-space: pre-wrap;
    padding: 20px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }
  
  .body-media {
    .media-desc {
      font-size: 15px;
      line-height: 1.9;
      color: #334155;
      margin-bottom: 20px;
      padding: 16px 20px;
      background: #f8fafc;
      border-radius: 10px;
      border-left: 4px solid #6366f1;
    }
    
    .media-img {
      max-width: 100%;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    .media-video {
      max-width: 100%;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
  }
  
  .body-link {
    .link-desc {
      font-size: 15px;
      line-height: 1.9;
      color: #334155;
      margin-bottom: 24px;
      padding: 16px 20px;
      background: #f8fafc;
      border-radius: 10px;
      border-left: 4px solid #6366f1;
    }
    
    .link-btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 14px 28px;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
      color: #fff;
      border-radius: 10px;
      text-decoration: none;
      font-size: 15px;
      font-weight: 600;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(99, 102, 241, 0.45);
      }
    }
  }
}

// 详情底部
.detail-foot {
  padding: 20px 28px;
  border-top: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.03);
  
  :deep(.ant-btn) {
    height: 48px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
    border: none;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.35);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
      background: linear-gradient(135deg, #818cf8 0%, #a78bfa 50%, #c084fc 100%);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

// 未选择状态
.no-select {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  
  .no-select-icon {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05), inset 0 2px 4px rgba(255, 255, 255, 0.8);
    animation: float 3s ease-in-out infinite;
    
    :deep(.anticon) {
      font-size: 42px;
      color: #94a3b8;
    }
  }
  
  .no-select-text {
    font-size: 15px;
    color: #64748b;
    margin: 0;
    font-weight: 500;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

// 福利展示区域
.benefits-section {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  border: 2px solid #f59e0b;
  
  .benefits-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: #b45309;
    margin-bottom: 16px;
    
    :deep(.anticon) {
      font-size: 20px;
      color: #f59e0b;
    }
  }
  
  .benefits-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    .benefit-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      :deep(.anticon) {
        font-size: 20px;
        color: #fff;
      }
    }
    
    .benefit-info {
      flex: 1;
      
      .benefit-name {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 4px;
      }
      
      .benefit-value {
        font-size: 18px;
        font-weight: 700;
        color: #f59e0b;
      }
      
      .benefit-details {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 4px;
      }
      
      .benefit-expire {
        font-size: 12px;
        color: #94a3b8;
        margin-top: 6px;
      }
    }
    
    &.gmoney .benefit-icon {
      background: linear-gradient(135deg, #f59e0b, #eab308);
    }
    
    &.dino .benefit-icon {
      background: linear-gradient(135deg, #22c55e, #16a34a);
    }
    
    &.ability .benefit-icon {
      background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    }
  }
}

// 领取按钮样式
.claim-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #eab308 100%) !important;
  border: none !important;
  
  &:hover {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%) !important;
  }
}

// 状态栏
.status-bar {
  padding: 14px 28px;
  background: linear-gradient(90deg, #dcfce7 0%, #d1fae5 50%, #cffafe 100%);
  border-top: 1px solid #a7f3d0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #22c55e, #10b981, #06b6d4);
  }
  
  &.warning {
    background: linear-gradient(90deg, #fef3c7 0%, #fef9c3 50%, #ffedd5 100%);
    border-top-color: #fde68a;
    
    &::before {
      background: linear-gradient(90deg, #f59e0b, #eab308, #f97316);
    }
    
    .status-text {
      color: #b45309;
      
      :deep(.anticon) {
        color: #f59e0b;
      }
    }
  }
  
  .status-text {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #15803d;
    
    :deep(.anticon) {
      font-size: 18px;
      color: #22c55e;
    }
    
    strong {
      font-weight: 700;
      color: #dc2626;
      background: linear-gradient(135deg, #fee2e2, #fecaca);
      padding: 2px 8px;
      border-radius: 4px;
    }
  }
}
</style>
